import { useMemo } from "react";
import { object, string, number, array } from "yup";
import type { BooleanSwitch } from "types";
import { BooleanSwitchValue } from "consts/general";
import { FormField } from "./types";

const useValidationSchema = () =>
    useMemo(() => (
        object().shape({
            [FormField.PreferredCurrency]: string()
                .nullable()
                .required()
                .label('Currency'),

            [FormField.AvgTransactionNoMonth]: number()
                .nullable()
                .required()
                .integer('Must be a whole number')
                .min(0, 'Must be at least 0')
                .max(1_000_000_000, 'Cannot exceed 1,000,000,000')
                .label('Average Number of Transactions'),

            [FormField.MaxTransactionVolumeMonth]: string()
                .nullable()
                .required()
                .label('Maximum Transactions Volume'),

            [FormField.MaxTransactionNoCustomerMonth]: number()
                .nullable()
                .required()
                .integer('Must be a whole number')
                .min(1, 'Must be at least 1')
                .max(1_000_000, 'Cannot exceed 1,000,000')
                .label('Maximum Number of Transactions per customer'),

            [FormField.EstimatedPercentChargebacksRefundsMonth]: number()
                .nullable()
                .required()
                .min(0, 'Must be at least 0%')
                .max(100, 'Cannot exceed 100%')
                .label('Estimated % of Chargebacks/Refunds'),

            [FormField.TransactionNoCurrentMonth]: number()
                .nullable()
                .required()
                .integer('Must be a whole number')
                .min(0, 'Must be at least 0')
                .max(1_000_000_000, 'Cannot exceed 1,000,000,000')
                .label('Number of transactions this month'),

            [FormField.AvgTransactionAmount]: number()
                .nullable()
                .required()
                .min(0.01, 'Must be at least 0.01')
                .label('Average Ticket Amount'),

            [FormField.MaxTransactionAmount]: number()
                .nullable()
                .required()
                .min(0.01, 'Must be at least 0.01')
                .test('min-avg-amount', 'Maximum ticket amount must be greater than or equal to average ticket amount', function (value) {
                    const avgAmount = this.parent[FormField.AvgTransactionAmount];

                    if (value == null || avgAmount == null) {
                        return true;
                    }

                    return value >= avgAmount;
                })
                .label('Maximum Ticket Amount'),

            [FormField.SalesVolumeCurrentMonth]: string()
                .nullable()
                .required()
                .label('Current month sales volume '),

            [FormField.ExpectedSalesVolumeMonth]: string()
                .nullable()
                .required()
                .label('Expected sales volume per month'),

            [FormField.IsProcessingHistoryOverSixMonths]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Processing history over six months'),

            [FormField.UsedPsps]: string()
                .nullable()
                .when(FormField.IsProcessingHistoryOverSixMonths, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.On),
                    then: validator => validator
                        .required()
                        .trim()
                        .min(3, 'Must be at least 3 characters')
                        .max(200, 'Cannot exceed 200 characters'),
                    otherwise: string()
                        .optional()
                })
                .label('Used PSPs'),

            [FormField.HasLeftPreviousProcessors]: number()
                .nullable()
                .required()
                .oneOf([BooleanSwitchValue.On, BooleanSwitchValue.Off], 'Select Yes or No')
                .label('Left previous processors'),

            [FormField.LeftPreviousProcessorsInput]: string()
                .nullable()
                .when(FormField.HasLeftPreviousProcessors, {
                    is: (value: BooleanSwitch) => Object.is(value, BooleanSwitchValue.On),
                    then: validator => validator
                        .required()
                        .trim()
                        .min(10, 'Must be at least 10 characters')
                        .max(500, 'Cannot exceed 500 characters'),
                    otherwise: string()
                        .optional()
                })
                .label('Details on leaving previous processors'),

            [FormField.SalesCurrencies]: array()
                .nullable()
                .required('Select at least one currency')
                .of(string())
                .min(1, 'Select at least one currency')
                .label('Sales Currencies'),

            [FormField.TotalTransactionAmountYearly]: number()
                .nullable()
                .required()
                .min(0.01, 'Must be at least 0.01')
                .label('Total Transaction Amount Yearly'),

            [FormField.CurrentPercentChargebacksRefundsRatio]: number()
                .nullable()
                .required()
                .min(0, 'Must be at least 0%')
                .max(100, 'Cannot exceed 100%')
                .label('Current % of Chargebacks/Refunds'),
        })
    ), []);

export default useValidationSchema;
